import { PaginatedResponse } from "shared/types/pagination";
import { config } from "./config";
import apiBase from "./misc/apiBase";
import { Enrollment } from "views/rent-reporting-onboarding/providers/enrollment/types";
import { Customer } from "providers/auth/types";
import { BankAccount } from "views/settings-linked-accounts/types";
import { PaymentInstrument } from "views/rent-reporting-onboarding/slices/plaid/types";
import { Token } from "@stripe/stripe-js/types/api";
import { CheckoutProduct } from "views/rent-reporting-onboarding/slices/checkout/types";
import { CustomerIssue } from "types/issue";
import { RentalVerification } from "types/rental-verifications";

const token = localStorage.getItem("token");
const authHeader = token ? { Authorization: "Bearer " + token } : {};
const headersConfig = {
  headers: { "Content-Type": "application/json", ...authHeader }
};

let instance = apiBase.create(config.apiHost, headersConfig);

const refreshInstance = (token?: string) => {
  const authHeader = token ? { Authorization: "Bearer " + token } : {};
  instance = apiBase.create(config.apiHost, {
    headers: { "Content-Type": "application/json", ...authHeader }
  });

  instance.interceptors.response.use((response) => {
    const { headers } = response;

    if (headers["x-api-key"]) {
      window?.localStorage?.setItem("token", headers["x-api-key"]);
      refreshInstance(headers["x-api-key"]);
    }

    return response;
  });
};

instance.interceptors.response.use((response) => {
  const { headers } = response;

  if (headers["x-api-key"]) {
    window?.localStorage?.setItem("token", headers["x-api-key"]);
    refreshInstance(headers["x-api-key"]);
  }

  return response;
});

const get = <T>(path: string, params?: object, opts?: object) => {
  return instance.get<T>(path, apiBase.mergeParams(params || {}, opts || {}));
};
const post = <T>(path: string, params?: object, opts?: object) => {
  return instance.post<T>(path, params, opts);
};
const patch = <T>(path: string, params?: object, opts?: object) => {
  return instance.patch<T>(path, params, opts);
};

const put = <T>(path: string, params?: object, opts?: object) => {
  return instance.put<T>(path, params, opts);
};

const del = <T>(path: string, params?: object, opts?: object) => {
  return instance.delete<T>(
    path,
    apiBase.mergeParams(params || {}, opts || {})
  );
};

const api = {
  get: get,
  post: post,
  put: put,
  delete: del,
  signOut: () => del("/v1/auth"),

  getCurrentCustomer: () => get<Customer>("/v1/me"),
  verifyCustomer: (data: { ssn: string; dob: string }) =>
    post<{ ssn: string; dob: string }>("/v1/me/id_verify", data),
  closeAccount: () => post("/v1/me/close_account"),

  getDashboard: () =>
    get<{
      customer_issues: CustomerIssue[];
      enrollments: Enrollment[];
      can_create_enrollment: boolean;
    }>("/v1/me/dashboard2"),
  getRentalVerficationsOfEnrollmentCase: (data: { id: number }) =>
    get<PaginatedResponse<RentalVerification>>(
      `/v1/enrollment_cases/${data.id}/rental_verifications`,
      data
    ),
  authWithLink: (data: { token: string }) =>
    post<Customer>(`/v1/auth/via_link/`, data),

  getPaymentInstruments: () =>
    get<PaginatedResponse<PaymentInstrument>>("/v1/payment_instruments"),
  getBankAccounts: () =>
    get<{
      bank_accounts: BankAccount[];
    }>("/v1/bank_accounts"),
  newPlaidLinkToken: () =>
    post<{
      link_token: string;
    }>("/v1/plaid/tokens/link"),
  exchangePlaidPublicToken: (data: object) =>
    post<{
      bank_account: BankAccount;
    }>("/v1/plaid/tokens/exchange", data),
  addStripeCardFromToken: (data: { token: Token }) =>
    post<{
      card: {
        // TODO: not sure about the response
        id: number;
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
      };
    }>("/v1/stripe/add_card_from_token", data),

  getCustomerDetails: () =>
    get<{ ssn?: string; dob?: string }>("/v1/me/customer_details"),

  getEnrollmentCases: () =>
    get<PaginatedResponse<Enrollment>>("/v1/enrollment_cases"),
  createEnrollmentCase: () => post("/v1/enrollment_cases"),
  setEnrollmentCaseAddress: (data: {
    id: number;
    address: { [key: string]: string };
  }) => patch(`/v1/enrollment_cases/${data.id}/address`, data),
  updateEnrollmentCase: (data: Partial<Enrollment> & { id: number }) =>
    patch(`/v1/enrollment_cases/${data.id}`, data),
  setEnrollmentCasePayee: (
    data: {
      id: number;
    } & Enrollment["legal_entity"]
  ) => patch(`/v1/enrollment_cases/${data.id}/payee`, data),
  addEnrollmentCaseDocuments: (data: { id: number; s3_url: string }) =>
    post(`/v1/enrollment_cases/${data.id}/add_documents`, data),
  subscribeReportEnrollment: (data: {
    products: string[];
    enrollment_case_id: number;
    payment_method_id: number;
    id?: number;
  }) =>
    post(
      `/v1/enrollment_cases/${data.id}/reporting_account_enrollment/subscribe`,
      data
    ),
  createPresignedUrlLeaseUpload: (data: { filename: string }) =>
    post<{
      presigned_url: string;
    }>(`/v1/documents/presign_put_url?filename=${data.filename}`),

  getCheckoutOptions: (data: { enrollment_case_id: number }) =>
    get<{
      products: CheckoutProduct[];
    }>("/v1/orders/checkout_options", data),
  checkout: (data: {
    products: string[];
    enrollment_case_id: number;
    payment_method_id: number;
  }) => post(`/v1/orders/checkout`, data),

  sendBankLinkUrl: () =>
    post<{
      plaid_connect_url: string;
    }>("/v1/bank_accounts/send_plaid_connect_url"),
  logout: () => del("/v1/auth"),
  bankTransactions: (
    enrollmentId: string | number,
    rentalVerificationId: string | number
  ) =>
    get<{
      bank_transactions: {
        id: string;
        name: string;
        categories: string[];
        amount: {
          cents: number;
          currency: string;
        };
        date: string;
      }[];
    }>(
      `/v1/reporting_account_enrollments/${enrollmentId}/rental_verifications/${rentalVerificationId}/bank_transactions`
    ),
  suggestBankTransaction: (
    enrollmentId: string | number,
    rvId: string | number,
    data: {
      transaction_ids?: string[];
      document?: {
        url: string;
        filename: string;
        description?: string;
      };
    }
  ) =>
    post(
      `/v1/reporting_account_enrollments/${enrollmentId}/rental_verifications/${rvId}/provide_confirmation`,
      data
    ),
  getUnverifiedRentalVerifications: (enrollmentId: string | number) =>
    get<{
      rental_verifications: {
        id: number;
        is_verified: boolean;
        for_month: string;
      }[];
    }>(
      `/v1/reporting_account_enrollments/${enrollmentId}/rental_verifications`
    ),
  createPaymentIntent: (data: {
    enrollment_case_id: number;
    products: string[];
  }) =>
    post<{
      client_secret: string;
    }>("/v1/stripe/create_payment_intent", data),
  checkoutWithPaymentIntentToken: (data: {
    stripe_token: string;
    enrollment_case_id: number;
  }) => post(`/v1/orders/checkout_with_stripe_payment_intent`, data),
  confirmMoveOut: (data: { id: number; moved_out_date: string }) =>
    post(`/v1/enrollment_cases/${data.id}/confirm_moved_out`, data),
  updateLeaseDetails: (data: {
    id: number;
    lease_end_date: string;
    monthly_rent_amount: {
      cents: number;
      currency: string;
    };
  }) => patch(`/v1/enrollment_cases/${data.id}/switch_to_lease_end_date`, data),
  renewLeaseOptions: (data: {
    id: number;
    renew_lease?: boolean;
    month_to_month_lease?: boolean;
    pending_lease_end_date?: string;
    pending_monthly_rent_amount?: {
      cents: number;
      currency: string;
    };
  }) => patch(`v1/enrollment_cases/${data.id}/end_lease_options`, data),
  // Soft delete. Also obfuscates all data associated with the customer
  unsubscribeFromReporting: () => post(`/v1/me/close_account`)
};

export default { ...apiBase, ...api, refreshInstance };
